import { useCallback, useEffect, useRef, useState } from 'react';
import { Color, GameState } from '@otrio/engine';
import { ClientMessage, LobbyState, PeekRoomPayload, ServerMessage } from '../protocol';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface UseGameSocketOptions {
  onConnected?: () => void;
  onRoomCreated?: (roomCode: string, lobby: LobbyState, yourColor: Color) => void;
  onRoomJoined?: (roomCode: string, lobby: LobbyState, yourColor: Color) => void;
  onLobbyPeek?: (lobby: LobbyState) => void;
  onLobbyUpdated?: (lobby: LobbyState) => void;
  onGameStarted?: (gameState: GameState, lobby: LobbyState) => void;
  onGameUpdated?: (gameState: GameState) => void;
  onColorClaimed?: (color: Color, lobby: LobbyState) => void;
}

interface UseGameSocketResult {
  status: ConnectionStatus;
  error: string | null;
  send: (msg: ClientMessage) => void;
}

const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

export function useGameSocket(options: UseGameSocketOptions = {}): UseGameSocketResult {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => {
      setStatus('connected');
      optionsRef.current.onConnected?.();
    };

    ws.onmessage = (event) => {
      const msg: ServerMessage = JSON.parse(event.data as string);
      switch (msg.type) {
        case 'ROOM_CREATED':
          optionsRef.current.onRoomCreated?.(msg.roomCode, msg.lobby, msg.yourColor);
          break;
        case 'ROOM_JOINED':
          optionsRef.current.onRoomJoined?.(msg.roomCode, msg.lobby, msg.yourColor);
          break;
        case 'LOBBY_PEEK':
          optionsRef.current.onLobbyPeek?.(msg.lobby);
          break;
        case 'LOBBY_UPDATED':
          optionsRef.current.onLobbyUpdated?.(msg.lobby);
          break;
        case 'GAME_STARTED':
          optionsRef.current.onGameStarted?.(msg.gameState, msg.lobby);
          break;
        case 'GAME_UPDATED':
          optionsRef.current.onGameUpdated?.(msg.gameState);
          break;
        case 'COLOR_CLAIMED':
          optionsRef.current.onColorClaimed?.(msg.color, msg.lobby);
          break;
        case 'ERROR':
          setError(msg.message);
          console.warn('[socket]', msg.message);
          break;
      }
    };

    ws.onerror = () => setStatus('error');
    ws.onclose = () => setStatus('idle');

    return () => ws.close();
  }, []);

  const send = useCallback((msg: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  return { status, error, send };
}

